/**
 * Invitation-gate client (issue #80).
 *
 * The code the visitor enters is held in sessionStorage across the Google OAuth
 * redirect: the sign-in flow is a full-page navigation to Google and back, so
 * component state does not survive it. sessionStorage (not localStorage) so an
 * abandoned attempt dies with the tab rather than lingering on the machine.
 */

const API_BASE_URL = 'https://api.1stprinciples.pro';

const PENDING_CODE_KEY = 'quantape.pendingInviteCode';

export const getPendingInviteCode = () => {
  try {
    return sessionStorage.getItem(PENDING_CODE_KEY) || '';
  } catch {
    return '';
  }
};

export const setPendingInviteCode = (code) => {
  try {
    if (code) sessionStorage.setItem(PENDING_CODE_KEY, code);
    else sessionStorage.removeItem(PENDING_CODE_KEY);
  } catch {
    /* private browsing — the claim just falls back to invite_required */
  }
};

export const clearPendingInviteCode = () => setPendingInviteCode('');

const postJson = async (path, body, { token } = {}) => {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body || {}),
  });
  const data = await response.json().catch(() => ({}));
  return { ok: response.ok, status: response.status, data };
};

/** Pre-flight a code before sending the visitor off to Google. */
export const checkInviteCode = async (code) => {
  const { data } = await postJson('/api/invite/check', { code });
  return data;
};

/** Ask the server whether this signed-in identity may enter, creating the account if a code backs it. */
export const claimAccess = async ({ token, inviteCode, name }) => {
  const { ok, status, data } = await postJson('/api/access/claim', { inviteCode, name }, { token });
  if (!ok && status === 401) return { allowed: false, reason: 'invalid_session' };
  return data;
};

export const joinWaitlist = async ({ email, name, reason }) => {
  const { ok, data } = await postJson('/api/waitlist', { email, name, reason });
  return { success: ok && data.success !== false, message: data.message };
};

/* ---- admin ---- */

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
});

export const fetchAdminWaitlist = async (status) => {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const response = await fetch(`${API_BASE_URL}/api/admin/waitlist${qs}`, { headers: authHeaders() });
  if (!response.ok) throw new Error('Failed to load waitlist');
  return (await response.json()).waitlist || [];
};

export const actOnWaitlistEntry = async (email, action) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/waitlist`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ email, action }),
  });
  if (!response.ok) throw new Error('Failed to update waitlist entry');
  return response.json();
};

export const fetchAdminInvites = async () => {
  const response = await fetch(`${API_BASE_URL}/api/admin/invites`, { headers: authHeaders() });
  if (!response.ok) throw new Error('Failed to load invites');
  return (await response.json()).invites || [];
};

export const createAdminInvites = async ({ count = 1, maxUses = 1, note, expiresAt } = {}) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/invites`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ count, maxUses, note, expiresAt }),
  });
  if (!response.ok) throw new Error('Failed to create invites');
  return (await response.json()).codes || [];
};

export const revokeAdminInvite = async (code) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/invites`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ action: 'revoke', code }),
  });
  if (!response.ok) throw new Error('Failed to revoke invite');
  return response.json();
};
